import { MessageItemAggregate } from '../message';
import { MessageGroupAggregate } from '../group/messageGroupModel';
import { isSystemMessage } from '../../integration/page/thread/common/threadCommonHelper';

const MESSAGE_GROUP_TIME_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Determines the effective role for a message, treating tool-only messages as system messages
 */
function getEffectiveMessageRole(
    messageItem: MessageItemAggregate
): 'user' | 'assistant' | 'system' {
    const { message } = messageItem;

    // User messages are always user
    if (message.role === 'user') {
        return 'user';
    }

    // For assistant messages, check if they should be treated as system messages
    if (message.role === 'assistant' && isSystemMessage(message.parts)) {
        return 'system';
    }

    // Default to the original role
    return message.role as 'user' | 'assistant' | 'system';
}

/**
 * Groups messages within a section by role and time proximity
 * This is the nested grouping within already date-grouped messages
 */
export function groupMessagesInSection(
    messages: MessageItemAggregate[]
): MessageGroupAggregate[] {
    if (messages.length === 0) return [];

    const groups: MessageGroupAggregate[] = [];
    let currentGroup: MessageGroupAggregate | null = null;
    let lastTimestamp: number | null = null;

    for (const messageItem of messages) {
        const message = messageItem.message;
        const effectiveRole = getEffectiveMessageRole(messageItem);

        const shouldCreateNewGroup =
            !currentGroup ||
            currentGroup.role !== effectiveRole ||
            (lastTimestamp &&
                message.createdAt.getTime() - lastTimestamp >
                    MESSAGE_GROUP_TIME_THRESHOLD_MS);

        if (shouldCreateNewGroup) {
            // Create new group
            currentGroup = {
                id: `group-${message.id}`,
                role: effectiveRole,
                definition: messageItem.definition,
                messages: [messageItem],
            };
            lastTimestamp = message.createdAt.getTime();
            groups.push(currentGroup);
        } else {
            // Add to existing group
            currentGroup!.messages.push(messageItem);
            lastTimestamp = message.createdAt.getTime();
        }
    }

    return groups;
}
